// Reading Realize, and every write it makes.
//
// TWO READS BEHIND ONE SCREEN, which is the shape this hook exists to hold: the
// list of maps, and the map that is open. They are separate requests because
// they are separate questions (`api/client.ts`), and holding both here is what
// lets the screen swap between them without a second component owning half the
// state.
//
// WHICH MAP IS OPEN IS SCREEN STATE, NOT A PATH. `shell/screens.ts` names
// screens, not rows, and `useShell` says so at the top of itself: "Should a
// screen ever want a parameter in its path, this is the file that grows one."
// Realize is the second screen to want one - Month wanted it first and answered
// the same way, keeping which month is on inside `useMonthScreen`. Growing a
// parameter into the shell for one screen is a change to how every screen is
// addressed, and it is not this feature's to make.
//
// EVERY WRITE SAYS WHAT IT DID, and every destructive one carries the Undo it
// owes (AGENTS.md). The toast is the only place to say it - there is no sheet on
// this screen - so nothing here swallows a failure.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ApiClientError,
  api,
  isSignedOut,
  messageFor,
  reasonFor,
  refusalRule,
} from '../api/client.ts';
import type {
  DeletedBranch,
  IsoDay,
  MapView,
  MindMap,
  MindNode,
  NodePosition,
} from '../api/client.ts';
import { CENTRE } from './realize-model.ts';
import type { PositionWrite } from './realize-model.ts';
import {
  readPendingCreations,
  writePendingBranchCreation,
  writePendingMapCreation,
} from './pending-creations.ts';
import type { PendingBranchCreation, PendingMapCreation } from './pending-creations.ts';

export interface RealizeHost {
  say: (message: string, undo?: () => void) => void;
  /** Takes the toast away, so an Undo cannot be pressed twice. */
  dismiss: () => void;
  /** Any 401, from this screen's own reads or from any write it makes. */
  signOut: () => void;
  /** False while another screen is on: nothing is fetched until this is true. */
  active: boolean;
}

export type RealizeComposing = 'add' | 'rename';

export interface RealizeRenameBasis {
  mapId: string;
  targetId: string;
  value: string;
  mapWriteVersion: string;
}

export interface RealizeComposer {
  mapId: string;
  draft: string;
  mode: RealizeComposing;
  targetId: string;
  targetHeading: string;
  renameBasis: RealizeRenameBasis | null;
  revision: number;
}

export interface RealizeState {
  /** The user's today, from the payload. Null until the first read lands. */
  today: IsoDay | null;
  /** Null until the list has been read once; kept while a later read is out. */
  maps: MindMap[] | null;
  deletedMaps: MindMap[];
  /** What went wrong with the LIST, said where the list would be. */
  problem: string | null;

  /** The map being worked in, or null when the list is what is on screen. */
  open: MapView | null;
  /** The id being read, which is set before the payload arrives. */
  openId: string | null;
  /** What went wrong with the MAP read. */
  mapProblem: string | null;

  /**
   * Which box the composer is aimed at. `CENTRE` when it is the topic itself,
   * which is where a map that has just been opened starts.
   */
  selected: string;
  select: (id: string) => void;
  composer: RealizeComposer | null;
  updateComposer: (
    mapId: string,
    update: (held: RealizeComposer | null) => RealizeComposer,
  ) => void;

  /** A write is in flight. The composer disables rather than disappearing. */
  busy: boolean;
  starterDraft: string;
  updateStarterDraft: (draft: string | ((held: string) => string)) => void;
  mapCreationPending: string | null;
  branchCreationPending: { mapId: string; targetId: string; label: string } | null;
  retryBranchCreation: () => Promise<boolean>;
  retryProblem: string | null;
  retryPending: () => void;
  retryFocus: RetryFocus | null;
  consumeRetryFocus: () => void;
  /** Maps whose confirmed deletion requests have not settled. */
  deletingMapIds: ReadonlySet<string>;
  /** Branches whose deletion requests have not settled. */
  deletingBranchIds: ReadonlySet<string>;

  openMap: (id: string) => void;
  closeMap: () => void;
  refresh: () => void;

  /** Answers whether the map was made, so a failed one keeps the typed topic. */
  startMap: (topic: string) => Promise<boolean>;
  /** Answers whether it landed, so the list knows a card is about to go. */
  removeMap: (map: MindMap) => Promise<boolean>;
  restoreMap: (map: MindMap) => Promise<void>;

  /** A branch under whatever is selected. Answers whether it landed. */
  addBranch: (label: string) => Promise<boolean>;
  /** Renames the selected box - the topic when it is the centre. */
  rename: (label: string, basis: RealizeRenameBasis) => Promise<RealizeRenameBasis | null>;
  /**
   * The selected branch and everything under it, with the Undo it owes.
   *
   * Answers with the box the selection LANDED on - the parent, or `CENTRE` for a
   * top-level branch - or null if nothing was deleted. The screen needs it to
   * put the cursor there: the box that had focus has just been unmounted.
   */
  removeBranch: () => Promise<string | null>;
  restoreBranch: (branch: DeletedBranch) => Promise<void>;

  /**
   * Put a branch where somebody dragged or nudged it.
   *
   * Optimistic, and deliberately so: the box has already followed the finger, so
   * a re-read that arrived a moment later would snap it back and then forward
   * again. A failure says so and re-reads, which puts it back where the server
   * actually has it.
   */
  moveBranch: (id: string, at: PositionWrite) => Promise<void>;
  /**
   * Put every branch back on the arrangement the client computes.
   *
   * Destructive - it throws away an arrangement somebody made by hand - so it
   * carries the Undo that every destructive action here owes, built out of what
   * the server answers with.
   *
   * It clears EVERY branch on the map, deleted ones included, so a branch
   * restored a week later does not reappear at an arbitrary spot in a map that
   * has since been arranged. `was` is the branches ON SCREEN - the live,
   * hand-placed ones - and it is what the sentence counts, because A NUMBER IN A
   * MESSAGE MUST COUNT WHAT THE PERSON CAN SEE. The server snapshots the wider
   * arrangement and the Undo posts back only its id.
   */
  tidyUp: (was: readonly NodePosition[]) => Promise<void>;
  undoTidy: () => void;
}

/** One box this client has moved, and whether the server has said so yet. */
interface PlacedByHand extends PositionWrite {
  acknowledged: boolean;
  sequence: number;
}

interface ConfirmedPosition {
  x: number | null;
  y: number | null;
  positionSpace: 'model' | 'normalized';
  anchorX: MindNode['anchorX'];
  anchorY: MindNode['anchorY'];
  renderDeltaX: MindNode['renderDeltaX'];
  renderDeltaY: MindNode['renderDeltaY'];
  renderOriginX: MindNode['renderOriginX'];
  renderOriginY: MindNode['renderOriginY'];
}

interface PendingTidyAction {
  id: string;
  priorActionId: string | null;
}

interface PendingCreationAction {
  key: string;
  id: string;
  navigation: number;
  outcomeUnknown: boolean;
}

interface PendingBranchCreationAction extends PendingCreationAction {
  mapId: string;
  parentId: string | null;
  label: string;
}

function storedMapCreation(action: PendingCreationAction | null): PendingMapCreation | null {
  return action === null ? null : { id: action.id, topic: action.key };
}

function storedBranchCreation(
  action: PendingBranchCreationAction | null,
): PendingBranchCreation | null {
  return action === null
    ? null
    : {
        id: action.id,
        mapId: action.mapId,
        parentId: action.parentId,
        label: action.label,
      };
}

interface DeletionGuard {
  mapId?: string;
  branchId?: string;
}

interface RetryFocus extends DeletionGuard {
  destination: 'resource' | 'composer';
}

interface PendingTransition {
  running: boolean;
  run: () => Promise<boolean>;
}

const NO_PLACEMENTS: ReadonlyMap<string, PlacedByHand> = new Map();

/** A payload, wearing the positions written since the read that fetched it. */
function overlay(body: MapView, placed: ReadonlyMap<string, PlacedByHand>): MapView {
  if (placed.size === 0) return body;
  let latest: PlacedByHand | undefined;
  for (const placement of placed.values()) {
    if (latest === undefined || placement.sequence > latest.sequence) latest = placement;
  }
  return {
    ...body,
    hasManualPositions: true,
    renderScale: latest?.renderScale ?? body.renderScale,
    renderOriginX: latest?.mapRenderOriginX ?? body.renderOriginX,
    renderOriginY: latest?.mapRenderOriginY ?? body.renderOriginY,
    renderSpan: latest?.mapRenderSpan ?? body.renderSpan,
    nodes: body.nodes.map((node: MindNode) => {
      const mine = placed.get(node.id);
      return mine === undefined
        ? node
        : {
            ...node,
            x: mine.x,
            y: mine.y,
            positionSpace: mine.positionSpace,
            anchorX: mine.anchorX,
            anchorY: mine.anchorY,
            renderDeltaX: mine.renderDeltaX,
            renderDeltaY: mine.renderDeltaY,
            renderOriginX: mine.renderOriginX,
            renderOriginY: mine.renderOriginY,
          };
    }),
  };
}

function terminalUndoFailure(error: unknown): boolean {
  return (
    refusalRule(error) === 'realize-action-superseded' ||
    (error instanceof ApiClientError && error.status === 404)
  );
}

function responseIsUnknown(error: unknown): boolean {
  return error instanceof ApiClientError && error.outcomeUnknown;
}

export function useRealize(host: RealizeHost): RealizeState {
  const { say, dismiss, signOut, active } = host;

  const [today, setToday] = useState<IsoDay | null>(null);
  const [maps, setMaps] = useState<MindMap[] | null>(null);
  const [deletedMaps, setDeletedMaps] = useState<MindMap[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [listRevision, setListRevision] = useState(0);
  const listAuthorityGeneration = useRef(0);

  const [openId, setOpenId] = useState<string | null>(null);
  const [open, setOpen] = useState<MapView | null>(null);
  const openNow = useRef(open);
  openNow.current = open;
  const mapWriteVersions = useRef(new Map<string, string>());
  if (open !== null) mapWriteVersions.current.set(open.map.id, open.map.writeVersion);
  const [mapProblem, setMapProblem] = useState<string | null>(null);
  const [mapRevision, setMapRevision] = useState(0);
  const mapReadGenerations = useRef(new Map<string, number>());
  const supersedeMapReads = useCallback((mapId: string) => {
    mapReadGenerations.current.set(mapId, (mapReadGenerations.current.get(mapId) ?? 0) + 1);
  }, []);
  const openIdNow = useRef(openId);
  openIdNow.current = openId;
  const activeNow = useRef(active);
  activeNow.current = active;
  const navigation = useRef(0);
  const accountId = useRef<string | null>(null);
  const accountGeneration = useRef(0);
  const mapCreationAction = useRef<PendingCreationAction | null>(null);
  const branchCreationAction = useRef<PendingBranchCreationAction | null>(null);
  const persistMapCreationAction = useCallback(
    (previous: PendingCreationAction | null = mapCreationAction.current): boolean => {
      const owner = accountId.current;
      if (owner === null) return false;
      return writePendingMapCreation(
        owner,
        storedMapCreation(previous),
        storedMapCreation(mapCreationAction.current),
      );
    },
    [],
  );
  const persistBranchCreationAction = useCallback(
    (previous: PendingBranchCreationAction | null = branchCreationAction.current): boolean => {
      const owner = accountId.current;
      if (owner === null) return false;
      return writePendingBranchCreation(
        owner,
        storedBranchCreation(previous),
        storedBranchCreation(branchCreationAction.current),
      );
    },
    [],
  );
  const rememberMapCreationAction = useCallback(
    (action: PendingCreationAction | null): boolean => {
      const before = mapCreationAction.current;
      mapCreationAction.current = action;
      if (persistMapCreationAction(before)) return true;
      mapCreationAction.current = before;
      return false;
    },
    [persistMapCreationAction],
  );
  const rememberBranchCreationAction = useCallback(
    (action: PendingBranchCreationAction | null): boolean => {
      const before = branchCreationAction.current;
      branchCreationAction.current = action;
      if (persistBranchCreationAction(before)) return true;
      branchCreationAction.current = before;
      return false;
    },
    [persistBranchCreationAction],
  );
  const [starterDraft, updateStarterDraft] = useState('');
  const [mapCreationPending, setMapCreationPending] = useState<string | null>(null);
  const [branchCreationPending, setBranchCreationPending] = useState<{
    mapId: string;
    targetId: string;
    label: string;
  } | null>(null);
  const hydrateStoredCreationActions = useCallback(() => {
    const owner = accountId.current;
    if (owner === null) return;
    const stored = readPendingCreations(owner);
    if (mapCreationAction.current === null && stored.map !== null) {
      mapCreationAction.current = {
        key: stored.map.topic,
        id: stored.map.id,
        navigation: navigation.current,
        outcomeUnknown: true,
      };
      updateStarterDraft(stored.map.topic);
      setMapCreationPending(stored.map.topic);
    }
    if (branchCreationAction.current === null && stored.branch !== null) {
      branchCreationAction.current = {
        key: JSON.stringify([stored.branch.mapId, stored.branch.parentId, stored.branch.label]),
        id: stored.branch.id,
        navigation: navigation.current,
        outcomeUnknown: true,
        mapId: stored.branch.mapId,
        parentId: stored.branch.parentId,
        label: stored.branch.label,
      };
      setBranchCreationPending({
        mapId: stored.branch.mapId,
        targetId: stored.branch.parentId ?? CENTRE,
        label: stored.branch.label,
      });
    }
  }, []);
  const tidyActions = useRef(new Map<string, PendingTidyAction>());
  const resolvePendingTidy = useCallback((view: MapView) => {
    const pending = tidyActions.current.get(view.map.id);
    const action = view.arrangementAction;
    if (
      pending !== undefined &&
      action !== null &&
      (action.actionId === pending.id || action.actionId !== pending.priorActionId)
    ) {
      tidyActions.current.delete(view.map.id);
    }
  }, []);

  const [selected, setSelected] = useState<string>(CENTRE);
  const selectedNow = useRef(selected);
  selectedNow.current = selected;
  const composers = useRef(new Map<string, RealizeComposer>());
  const [, setComposerRevision] = useState(0);
  const updateComposer = useCallback(
    (mapId: string, update: (held: RealizeComposer | null) => RealizeComposer) => {
      const before = composers.current.get(mapId) ?? null;
      const after = update(before);
      if (after === before) return;
      composers.current.set(mapId, after);
      setComposerRevision((revision) => revision + 1);
    },
    [],
  );
  const [busy, setBusy] = useState(false);
  const busyWrites = useRef(0);
  const [deletingMapIds, setDeletingMapIds] = useState<ReadonlySet<string>>(() => new Set());
  const [deletingBranchIds, setDeletingBranchIds] = useState<ReadonlySet<string>>(() => new Set());
  const pendingMapDeletions = useRef(new Map<string, number>());
  const pendingBranchDeletions = useRef(new Map<string, number>());
  const pendingTransition = useRef<PendingTransition | null>(null);
  const pendingTransitionWaiters = useRef(new Set<() => void>());
  const [retryProblem, setRetryProblem] = useState<string | null>(null);
  const [retryFocus, setRetryFocus] = useState<RetryFocus | null>(null);
  const consumeRetryFocus = useCallback(() => setRetryFocus(null), []);
  const clearPendingTransition = useCallback((task: PendingTransition): boolean => {
    if (pendingTransition.current !== task) return false;
    pendingTransition.current = null;
    setRetryProblem(null);
    const waiting = [...pendingTransitionWaiters.current];
    pendingTransitionWaiters.current.clear();
    for (const resume of waiting) resume();
    return true;
  }, []);
  const waitForPendingTransition = useCallback(async (): Promise<void> => {
    while (pendingTransition.current !== null) {
      await new Promise<void>((resolve) => pendingTransitionWaiters.current.add(resolve));
    }
  }, []);
  const beginDeletion = useCallback((guard: DeletionGuard) => {
    if (guard.mapId !== undefined) {
      const count = pendingMapDeletions.current.get(guard.mapId) ?? 0;
      pendingMapDeletions.current.set(guard.mapId, count + 1);
      setDeletingMapIds(new Set(pendingMapDeletions.current.keys()));
    }
    if (guard.branchId !== undefined) {
      const count = pendingBranchDeletions.current.get(guard.branchId) ?? 0;
      pendingBranchDeletions.current.set(guard.branchId, count + 1);
      setDeletingBranchIds(new Set(pendingBranchDeletions.current.keys()));
    }
  }, []);
  const finishDeletion = useCallback((guard: DeletionGuard) => {
    if (guard.mapId !== undefined) {
      const count = pendingMapDeletions.current.get(guard.mapId) ?? 0;
      if (count <= 1) pendingMapDeletions.current.delete(guard.mapId);
      else pendingMapDeletions.current.set(guard.mapId, count - 1);
      setDeletingMapIds(new Set(pendingMapDeletions.current.keys()));
    }
    if (guard.branchId !== undefined) {
      const count = pendingBranchDeletions.current.get(guard.branchId) ?? 0;
      if (count <= 1) pendingBranchDeletions.current.delete(guard.branchId);
      else pendingBranchDeletions.current.set(guard.branchId, count - 1);
      setDeletingBranchIds(new Set(pendingBranchDeletions.current.keys()));
    }
  }, []);
  const selectedHere =
    selected === CENTRE || open === null || open.nodes.some((node) => node.id === selected)
      ? selected
      : CENTRE;
  const composer =
    openId === null
      ? null
      : (composers.current.get(openId) ?? {
          mapId: openId,
          draft: branchCreationPending?.mapId === openId ? branchCreationPending.label : '',
          mode: 'add',
          targetId:
            branchCreationPending?.mapId === openId ? branchCreationPending.targetId : selectedHere,
          targetHeading: '',
          renameBasis: null,
          revision: 0,
        });

  // Read through a ref so the effects below depend on WHICH read is wanted
  // rather than on how this screen's caller happens to be rendered: `signOut`
  // is a fresh closure every render, and depending on it would re-read the
  // whole list whenever anything in the shell re-renders.
  const latest = useRef(signOut);
  latest.current = signOut;

  useEffect(() => {
    navigation.current += 1;
  }, [active]);

  // WHERE THIS CLIENT HAS PUT A BOX SINCE THE READ THAT IS IN THE AIR WENT OUT.
  //
  // Every other write here re-reads the map, and a read answers with the state
  // the server held when it was ISSUED. So a drag finished while one of those
  // reads is in flight is a position the payload cannot know about, and letting
  // the payload land on top of it puts the box back at its computed place while
  // the server holds it where somebody dropped it - a drag that "sometimes does
  // not stick", which is the worst shape a defect can take because nobody can
  // reproduce it on demand.
  //
  // So a moved branch is held here and laid back over every payload until a read
  // ISSUED AFTER THE SERVER ACKNOWLEDGED IT lands - that read is the first one
  // that can carry it, and from then on the payload is the truth. Dropping it on
  // agreement instead would never let go when another tab moved the same box.
  const placed = useRef(new Map<string, Map<string, PlacedByHand>>());
  const placementSequence = useRef(0);
  const confirmedPositions = useRef(new Map<string, Map<string, ConfirmedPosition>>());
  const confirmedManualPositions = useRef(new Map<string, boolean>());
  const rememberConfirmedView = useCallback(
    (view: MapView, manualStateIsCurrent: boolean, preservePendingPositions = true) => {
      const mapId = view.map.id;
      const pending = placed.current.get(mapId);
      const before = confirmedPositions.current.get(mapId) ?? new Map();
      const next = new Map<string, ConfirmedPosition>();
      for (const node of view.nodes) {
        const prior = before.get(node.id);
        if (preservePendingPositions && pending?.has(node.id) && prior !== undefined) {
          next.set(node.id, prior);
        } else {
          next.set(node.id, {
            x: node.x,
            y: node.y,
            positionSpace: node.positionSpace,
            anchorX: node.anchorX,
            anchorY: node.anchorY,
            renderDeltaX: node.renderDeltaX,
            renderDeltaY: node.renderDeltaY,
            renderOriginX: node.renderOriginX,
            renderOriginY: node.renderOriginY,
          });
        }
      }
      confirmedPositions.current.set(mapId, next);
      if (manualStateIsCurrent) {
        confirmedManualPositions.current.set(mapId, view.hasManualPositions);
      }
    },
    [],
  );
  const releaseClosedMapState = useCallback((mapId: string) => {
    if (openIdNow.current === mapId) return;
    const mapPlacements = placed.current.get(mapId);
    let hasUnacknowledged = false;
    if (mapPlacements !== undefined) {
      for (const [id, placement] of mapPlacements) {
        if (placement.acknowledged) mapPlacements.delete(id);
        else hasUnacknowledged = true;
      }
      if (mapPlacements.size === 0) placed.current.delete(mapId);
    }
    if (!hasUnacknowledged) {
      confirmedPositions.current.delete(mapId);
      confirmedManualPositions.current.delete(mapId);
    }
  }, []);
  const mutationWrites = useRef<Promise<void>>(Promise.resolve());
  const enqueueMutation = useCallback(
    <T>(work: () => Promise<T>): Promise<T> => {
      const queued = mutationWrites.current.then(work);
      mutationWrites.current = queued.then(
        () => waitForPendingTransition(),
        () => waitForPendingTransition(),
      );
      return queued;
    },
    [waitForPendingTransition],
  );
  const withBusyDirect = useCallback(async <T>(work: () => Promise<T>): Promise<T> => {
    busyWrites.current += 1;
    setBusy(true);
    try {
      return await work();
    } finally {
      busyWrites.current -= 1;
      if (busyWrites.current === 0) setBusy(false);
    }
  }, []);
  const withBusyMutation = useCallback(
    <T>(work: () => Promise<T>): Promise<T> => withBusyDirect(() => enqueueMutation(work)),
    [enqueueMutation, withBusyDirect],
  );

  const acceptAccount = useCallback(
    (owner: string) => {
      const previousOwner = accountId.current;
      if (previousOwner === owner) return;

      accountGeneration.current += 1;
      accountId.current = owner;
      navigation.current += 1;
      listAuthorityGeneration.current += 1;
      mapReadGenerations.current.clear();
      mapWriteVersions.current.clear();
      tidyActions.current.clear();
      composers.current.clear();
      placed.current.clear();
      confirmedPositions.current.clear();
      confirmedManualPositions.current.clear();
      pendingMapDeletions.current.clear();
      pendingBranchDeletions.current.clear();
      pendingTransition.current = null;
      const waiting = [...pendingTransitionWaiters.current];
      pendingTransitionWaiters.current.clear();
      for (const resume of waiting) resume();

      openIdNow.current = null;
      openNow.current = null;
      selectedNow.current = CENTRE;
      setToday(null);
      setMaps(null);
      setDeletedMaps([]);
      setProblem(null);
      setOpenId(null);
      setOpen(null);
      setMapProblem(null);
      setSelected(CENTRE);
      setComposerRevision((revision) => revision + 1);
      setDeletingMapIds(new Set());
      setDeletingBranchIds(new Set());
      setRetryProblem(null);
      setRetryFocus(null);
      if (previousOwner !== null) dismiss();

      const stored = readPendingCreations(owner);
      mapCreationAction.current =
        stored.map === null
          ? null
          : {
              key: stored.map.topic,
              id: stored.map.id,
              navigation: navigation.current,
              outcomeUnknown: true,
            };
      branchCreationAction.current =
        stored.branch === null
          ? null
          : {
              key: JSON.stringify([
                stored.branch.mapId,
                stored.branch.parentId,
                stored.branch.label,
              ]),
              id: stored.branch.id,
              navigation: navigation.current,
              outcomeUnknown: true,
              mapId: stored.branch.mapId,
              parentId: stored.branch.parentId,
              label: stored.branch.label,
            };
      if (stored.map !== null) updateStarterDraft(stored.map.topic);
      else if (previousOwner !== null) updateStarterDraft('');
      setMapCreationPending(stored.map?.topic ?? null);
      setBranchCreationPending(
        stored.branch === null
          ? null
          : {
              mapId: stored.branch.mapId,
              targetId: stored.branch.parentId ?? CENTRE,
              label: stored.branch.label,
            },
      );
    },
    [dismiss],
  );

  const acceptDeletedMap = useCallback(
    (mapId: string, known?: MindMap) => {
      listAuthorityGeneration.current += 1;
      supersedeMapReads(mapId);
      tidyActions.current.delete(mapId);
      if (known === undefined) {
        mapWriteVersions.current.delete(mapId);
        setMaps(null);
        setDeletedMaps([]);
        setProblem(null);
      } else {
        const deleted = { ...known, deleted: true };
        mapWriteVersions.current.set(mapId, deleted.writeVersion);
        setMaps((held) => held?.filter((map) => map.id !== mapId) ?? held);
        setDeletedMaps((held) => [deleted, ...held.filter((map) => map.id !== mapId)]);
      }
      if (openIdNow.current === mapId && openNow.current?.map.id === mapId) {
        setOpen((held) =>
          held?.map.id === mapId
            ? { ...held, map: { ...(known ?? held.map), deleted: true } }
            : held,
        );
        setMapProblem('That map was deleted. Put it back before changing it.');
      } else {
        placed.current.delete(mapId);
        confirmedPositions.current.delete(mapId);
        confirmedManualPositions.current.delete(mapId);
        if (openIdNow.current === mapId) {
          navigation.current += 1;
          openIdNow.current = null;
          selectedNow.current = CENTRE;
          setSelected(CENTRE);
          setOpenId(null);
          setOpen(null);
          setMapProblem(null);
        }
      }
      setListRevision((revision) => revision + 1);
    },
    [supersedeMapReads],
  );

  const reconcileStaleState = useCallback(
    (error: unknown, mapId: string | undefined) => {
      const rule = refusalRule(error);
      if (rule === 'realize-account-changed') {
        listAuthorityGeneration.current += 1;
        setMaps(null);
        setDeletedMaps([]);
        setProblem(null);
        setListRevision((revision) => revision + 1);
        return;
      }
      if (mapId === undefined) return;
      if (rule === 'mind-map-deleted') {
        acceptDeletedMap(mapId);
        return;
      }
      if (
        rule === 'branch-deleted' ||
        rule === 'branch-parent-deleted' ||
        rule === 'realize-action-superseded'
      ) {
        listAuthorityGeneration.current += 1;
        supersedeMapReads(mapId);
        mapWriteVersions.current.delete(mapId);
        setMaps(null);
        setDeletedMaps([]);
        setProblem(null);
        setListRevision((revision) => revision + 1);
        if (openIdNow.current === mapId) {
          setOpen(null);
          setMapProblem(null);
          setMapRevision((revision) => revision + 1);
        }
      }
    },
    [acceptDeletedMap, supersedeMapReads],
  );

  useEffect(() => {
    if (!active) return;
    let live = true;
    const authorityAtStart = listAuthorityGeneration.current;
    void (async () => {
      try {
        const body = await api.maps();
        if (!live) return;
        if (listAuthorityGeneration.current !== authorityAtStart) {
          setListRevision((revision) => revision + 1);
          return;
        }
        acceptAccount(body.accountId);
        hydrateStoredCreationActions();
        setToday(body.today);
        for (const map of [...body.maps, ...body.deleted]) {
          mapWriteVersions.current.set(map.id, map.writeVersion);
        }
        setMaps(body.maps);
        setDeletedMaps(body.deleted);
        setProblem(null);
      } catch (error) {
        if (isSignedOut(error)) {
          latest.current();
          return;
        }
        // Whatever was on screen is kept: it may still be right, and replacing
        // it with a sentence loses what was readable.
        if (live) setProblem(messageFor(error));
      }
    })();
    return () => {
      live = false;
    };
  }, [acceptAccount, active, hydrateStoredCreationActions, listRevision]);

  useEffect(() => {
    if (!active || openId === null) return;
    let live = true;
    // Taken BEFORE the request goes out, and by identity rather than by id: a
    // branch moved again while this read is in flight is a different entry, and
    // must not be let go on the strength of the write before it.
    const mapPlacements = placed.current.get(openId);
    const carried = new Set([...(mapPlacements?.values() ?? [])].filter((one) => one.acknowledged));
    const readGeneration = mapReadGenerations.current.get(openId) ?? 0;
    const accountAtStart = accountGeneration.current;
    void (async () => {
      try {
        const body = await api.map(openId);
        if (
          !live ||
          accountGeneration.current !== accountAtStart ||
          openIdNow.current !== openId ||
          (mapReadGenerations.current.get(openId) ?? 0) !== readGeneration
        )
          return;
        if (body.map.deleted) {
          acceptDeletedMap(openId, body.map);
          return;
        }
        listAuthorityGeneration.current += 1;
        resolvePendingTidy(body);
        const current = placed.current.get(openId);
        if (current !== undefined) {
          for (const [id, one] of current) {
            if (carried.has(one)) current.delete(id);
          }
          if (current.size === 0) placed.current.delete(openId);
        }
        setSelected((held) =>
          held === CENTRE || body.nodes.some((node) => node.id === held) ? held : CENTRE,
        );
        const hasCurrentPlacements = current !== undefined && current.size > 0;
        const accepted = hasCurrentPlacements
          ? {
              ...body,
              map: {
                ...body.map,
                writeVersion: mapWriteVersions.current.get(body.map.id) ?? body.map.writeVersion,
              },
            }
          : body;
        rememberConfirmedView(accepted, !hasCurrentPlacements);
        mapWriteVersions.current.set(accepted.map.id, accepted.map.writeVersion);
        setOpen(overlay(accepted, current ?? NO_PLACEMENTS));
        setMapProblem(null);
      } catch (error) {
        if (isSignedOut(error)) {
          latest.current();
          return;
        }
        if (
          live &&
          accountGeneration.current === accountAtStart &&
          (mapReadGenerations.current.get(openId) ?? 0) === readGeneration
        ) {
          setMapProblem(messageFor(error));
        }
      }
    })();
    return () => {
      live = false;
    };
  }, [acceptDeletedMap, active, openId, mapRevision, rememberConfirmedView, resolvePendingTidy]);

  const refresh = useCallback(() => {
    setListRevision((n) => n + 1);
    setMapRevision((n) => n + 1);
  }, []);

  const invalidateMap = useCallback(
    (mapId: string) => {
      if (openIdNow.current === mapId) {
        setMapRevision((n) => n + 1);
      } else {
        releaseClosedMapState(mapId);
        setListRevision((n) => n + 1);
      }
    },
    [releaseClosedMapState],
  );

  const openMap = useCallback((id: string) => {
    if (pendingMapDeletions.current.has(id)) return;
    navigation.current += 1;
    openIdNow.current = id;
    setOpenId(id);
    setOpen(null);
    setMapProblem(null);
    const target = composers.current.get(id)?.targetId ?? CENTRE;
    selectedNow.current = target;
    setSelected(target);
  }, []);

  const closeMap = useCallback(() => {
    navigation.current += 1;
    const closing = openIdNow.current;
    openIdNow.current = null;
    setOpenId(null);
    setOpen(null);
    setMapProblem(null);
    setListRevision((n) => n + 1);
    if (closing !== null) releaseClosedMapState(closing);
  }, [releaseClosedMapState]);

  const select = useCallback((id: string) => {
    selectedNow.current = id;
    setSelected(id);
  }, []);

  const runRecoverableTransition = useCallback(
    async <T>(
      guard: DeletionGuard,
      request: () => Promise<T>,
      accepted: (value: T) => void,
      failed: (why: string) => string,
      mapId?: string,
    ): Promise<boolean> => {
      if (pendingTransition.current !== null) return false;
      const accountAtStart = accountGeneration.current;
      beginDeletion(guard);
      let finished = false;
      const finish = (): void => {
        if (finished) return;
        finished = true;
        finishDeletion(guard);
      };
      const task: PendingTransition = {
        running: false,
        run: async () => false,
      };
      task.run = async (): Promise<boolean> => {
        if (task.running || finished) return false;
        task.running = true;
        const mutate = pendingTransition.current === task ? withBusyDirect : withBusyMutation;
        try {
          return await mutate(async () => {
            if (accountGeneration.current !== accountAtStart) {
              clearPendingTransition(task);
              finish();
              return false;
            }
            try {
              const value = await request();
              if (accountGeneration.current !== accountAtStart) {
                clearPendingTransition(task);
                finish();
                return false;
              }
              accepted(value);
              const resolvedUnknown = clearPendingTransition(task);
              if (resolvedUnknown) {
                setRetryFocus({
                  ...guard,
                  ...(mapId === undefined ? {} : { mapId }),
                  destination: 'resource',
                });
              }
              finish();
              return true;
            } catch (error) {
              if (accountGeneration.current !== accountAtStart) {
                clearPendingTransition(task);
                finish();
                return false;
              }
              if (isSignedOut(error)) {
                clearPendingTransition(task);
                finish();
                signOut();
                return false;
              }
              reconcileStaleState(error, mapId);
              const message = failed(messageFor(error));
              if (responseIsUnknown(error)) {
                pendingTransition.current = task;
                setRetryProblem(message);
                say(`${message} Try again to finish checking it.`);
              } else {
                clearPendingTransition(task);
                finish();
                say(message);
              }
              return false;
            }
          });
        } finally {
          task.running = false;
        }
      };
      return task.run();
    },
    [
      beginDeletion,
      clearPendingTransition,
      finishDeletion,
      reconcileStaleState,
      say,
      signOut,
      withBusyDirect,
      withBusyMutation,
    ],
  );

  const retryPending = useCallback(() => {
    const pending = pendingTransition.current;
    if (pending !== null) void pending.run();
  }, []);

  /**
   * The Undo on a toast, run with no sheet on screen - so its own failure has
   * nowhere to go but the toast, and it has to be said: an undo that quietly
   * does nothing is worse than no undo at all.
   *
   * The line goes first, before the request. A button left up while the write
   * behind it is in flight is a button that can be pressed twice, and the second
   * press arrives at something already restored and is reported as a failure for
   * the undo that worked (`undoWith` in notes/useNotes.ts, same reasoning).
   */
  const undoWith = useCallback(
    (work: () => Promise<unknown>, mapId?: string, guard?: DeletionGuard) => {
      const run = () => {
        const accountAtStart = accountGeneration.current;
        dismiss();
        if (guard !== undefined) {
          void runRecoverableTransition(
            guard,
            work,
            refresh,
            (why) => `That could not be undone. ${why}`,
            mapId,
          );
          return;
        }
        void (async () => {
          try {
            await withBusyMutation(async () => {
              if (accountGeneration.current !== accountAtStart) return;
              await work();
            });
            if (accountGeneration.current !== accountAtStart) return;
            refresh();
          } catch (error) {
            if (accountGeneration.current !== accountAtStart) return;
            if (isSignedOut(error)) {
              signOut();
              return;
            }
            const terminal = terminalUndoFailure(error);
            reconcileStaleState(error, mapId);
            say(`That could not be undone. ${messageFor(error)}`, terminal ? undefined : run);
          }
        })();
      };
      return run;
    },
    [
      dismiss,
      reconcileStaleState,
      refresh,
      runRecoverableTransition,
      say,
      signOut,
      withBusyMutation,
    ],
  );

  /** One write, with the two failures every write here has in common. */
  const write = useCallback(
    async <T>(
      work: () => Promise<T>,
      failed: (why: string) => string,
      mapId?: string,
      onError?: (error: unknown) => void,
    ): Promise<T | null> => {
      const accountAtStart = accountGeneration.current;
      try {
        return await withBusyMutation(async () => {
          if (accountGeneration.current !== accountAtStart) return null;
          const value = await work();
          return accountGeneration.current === accountAtStart ? value : null;
        });
      } catch (error) {
        if (accountGeneration.current !== accountAtStart) return null;
        if (isSignedOut(error)) {
          signOut();
          return null;
        }
        onError?.(error);
        reconcileStaleState(error, mapId);
        say(failed(responseIsUnknown(error) ? messageFor(error) : reasonFor(error)));
        return null;
      }
    },
    [reconcileStaleState, say, signOut, withBusyMutation],
  );

  const writeRetryably = useCallback(
    <T>(
      work: () => Promise<T>,
      failed: (why: string) => string,
      uncertain: (why: string) => string,
      mapId?: string,
      retryDestination?: RetryFocus['destination'],
      busyInitially = true,
      accepted?: (value: T) => void,
    ): Promise<T | null> => {
      if (pendingTransition.current !== null) return Promise.resolve(null);
      const accountAtStart = accountGeneration.current;

      let settle: (value: T | null) => void = () => undefined;
      const completion = new Promise<T | null>((resolve) => {
        settle = resolve;
      });
      let settled = false;
      const finish = (value: T | null): void => {
        if (settled) return;
        settled = true;
        settle(value);
      };
      let firstAttempt = true;
      const task: PendingTransition = {
        running: false,
        run: async () => false,
      };
      task.run = async (): Promise<boolean> => {
        if (task.running || settled) return false;
        task.running = true;
        const retrying = pendingTransition.current === task;
        const mutate = retrying
          ? withBusyDirect
          : firstAttempt && !busyInitially
            ? enqueueMutation
            : withBusyMutation;
        firstAttempt = false;
        try {
          return await mutate(async () => {
            if (accountGeneration.current !== accountAtStart) {
              clearPendingTransition(task);
              finish(null);
              return false;
            }
            try {
              const value = await work();
              if (accountGeneration.current !== accountAtStart) {
                clearPendingTransition(task);
                finish(null);
                return false;
              }
              accepted?.(value);
              const resolvedUnknown = clearPendingTransition(task);
              finish(value);
              if (resolvedUnknown && mapId !== undefined && retryDestination !== undefined) {
                queueMicrotask(() => setRetryFocus({ mapId, destination: retryDestination }));
              }
              return true;
            } catch (error) {
              if (accountGeneration.current !== accountAtStart) {
                clearPendingTransition(task);
                finish(null);
                return false;
              }
              if (isSignedOut(error)) {
                clearPendingTransition(task);
                finish(null);
                signOut();
                return false;
              }
              reconcileStaleState(error, mapId);
              if (responseIsUnknown(error)) {
                const message = uncertain(messageFor(error));
                pendingTransition.current = task;
                setRetryProblem(message);
                say(`${message} Try again to finish checking it.`);
              } else {
                clearPendingTransition(task);
                say(failed(reasonFor(error)));
                finish(null);
              }
              return false;
            }
          });
        } finally {
          task.running = false;
        }
      };
      void task.run();
      return completion;
    },
    [
      clearPendingTransition,
      enqueueMutation,
      reconcileStaleState,
      say,
      signOut,
      withBusyDirect,
      withBusyMutation,
    ],
  );

  /**
   * MOVE a map between the two lists this screen holds as soon as the write lands,
   * because A RECOVERABLE DELETE IS
   * NOT A DESTRUCTION, it is a row changing which list it is on.
   *
   * Dropping it from both would draw a state the account is not in. On an
   * account with one map, `maps` and `deletedMaps` are then both empty for a
   * whole round trip, and that is the branch `MapList` shows the WELCOME on -
   * one field and an invitation to start, said to somebody who has just deleted
   * something and is already wondering whether they meant to, and gone again
   * before they can read it. Moving the row instead puts the Deleted group up
   * with `Restore` in it, which is the way back on screen rather than only on a
   * toast that expires.
   *
   * The row is the SERVER'S answer rather than the one this client was holding,
   * and it goes to the TOP because both writes touch `updated_at` and the list
   * is ordered by it (`api/src/db/repositories/realize.ts`) - so the order drawn
   * here is the order the re-read lands with.
   */
  const moveMap = useCallback(
    (map: MindMap) => {
      listAuthorityGeneration.current += 1;
      supersedeMapReads(map.id);
      mapWriteVersions.current.set(map.id, map.writeVersion);
      const without = (held: MindMap[]): MindMap[] => held.filter((one) => one.id !== map.id);
      setMaps((held) =>
        held === null ? held : map.deleted ? without(held) : [map, ...without(held)],
      );
      setDeletedMaps((held) => (map.deleted ? [map, ...without(held)] : without(held)));
    },
    [supersedeMapReads],
  );

  const rememberMap = useCallback(
    (map: MindMap) => {
      mapWriteVersions.current.set(map.id, map.writeVersion);
      setOpen((held) => (held === null || held.map.id !== map.id ? held : { ...held, map }));
      moveMap(map);
    },
    [moveMap],
  );

  const rememberMapWriteVersion = useCallback(
    (mapId: string, writeVersion: string) => {
      listAuthorityGeneration.current += 1;
      supersedeMapReads(mapId);
      mapWriteVersions.current.set(mapId, writeVersion);
      const withVersion = (map: MindMap): MindMap =>
        map.id === mapId ? { ...map, writeVersion } : map;
      setOpen((held) =>
        held === null || held.map.id !== mapId ? held : { ...held, map: withVersion(held.map) },
      );
      setMaps((held) => held?.map(withVersion) ?? held);
      setDeletedMaps((held) => held.map(withVersion));
    },
    [supersedeMapReads],
  );

  const rememberNode = useCallback((mapId: string, node: MindNode, addIfMissing: boolean) => {
    if (!placed.current.get(mapId)?.has(node.id)) {
      let confirmed = confirmedPositions.current.get(mapId);
      if (confirmed === undefined) {
        confirmed = new Map();
        confirmedPositions.current.set(mapId, confirmed);
      }
      confirmed.set(node.id, {
        x: node.x,
        y: node.y,
        positionSpace: node.positionSpace,
        anchorX: node.anchorX,
        anchorY: node.anchorY,
        renderDeltaX: node.renderDeltaX,
        renderDeltaY: node.renderDeltaY,
        renderOriginX: node.renderOriginX,
        renderOriginY: node.renderOriginY,
      });
    }
    setOpen((held) => {
      if (held === null || held.map.id !== mapId) return held;
      const at = held.nodes.findIndex((one) => one.id === node.id);
      if (at === -1 && !addIfMissing) return held;
      const nodes =
        at === -1
          ? [...held.nodes, node]
          : held.nodes.map((one) => (one.id === node.id ? node : one));
      return overlay({ ...held, nodes }, placed.current.get(mapId) ?? NO_PLACEMENTS);
    });
  }, []);

  const rememberView = useCallback(
    (view: MapView, missingSelection: string = CENTRE, arrangementIsAuthoritative = false) => {
      resolvePendingTidy(view);
      moveMap(view.map);
      const mapId = view.map.id;
      const current = placed.current.get(mapId);
      if (current !== undefined) {
        const visible = new Set(view.nodes.map((node) => node.id));
        for (const id of current.keys()) {
          if (!visible.has(id)) current.delete(id);
        }
        if (current.size === 0) placed.current.delete(mapId);
      }
      rememberConfirmedView(view, true, !arrangementIsAuthoritative);
      mapWriteVersions.current.set(mapId, view.map.writeVersion);
      if (openIdNow.current !== mapId) {
        releaseClosedMapState(mapId);
        return;
      }
      const fallback =
        missingSelection === CENTRE || view.nodes.some((node) => node.id === missingSelection)
          ? missingSelection
          : CENTRE;
      setSelected((held) =>
        held === CENTRE || view.nodes.some((node) => node.id === held) ? held : fallback,
      );
      setOpen(overlay(view, placed.current.get(mapId) ?? NO_PLACEMENTS));
      setMapProblem(null);
    },
    [moveMap, releaseClosedMapState, rememberConfirmedView, resolvePendingTidy],
  );

  const closeMapIfOpen = useCallback((mapId: string) => {
    placed.current.delete(mapId);
    confirmedPositions.current.delete(mapId);
    confirmedManualPositions.current.delete(mapId);
    if (openIdNow.current === mapId) openIdNow.current = null;
    setOpenId((held) => (held === mapId ? null : held));
    setOpen((held) => (held?.map.id === mapId ? null : held));
  }, []);

  const undoArrangement = useCallback(
    function run(mapId: string, undoId: string): void {
      const accountAtStart = accountGeneration.current;
      tidyActions.current.delete(mapId);
      dismiss();
      void (async () => {
        try {
          const view = await withBusyMutation(async () => {
            if (accountGeneration.current !== accountAtStart) return null;
            const restored = await api.restoreArrangement(mapId, undoId);
            return accountGeneration.current === accountAtStart ? restored : null;
          });
          if (view === null) return;
          rememberView(view, CENTRE, true);
          invalidateMap(mapId);
        } catch (error) {
          if (accountGeneration.current !== accountAtStart) return;
          if (isSignedOut(error)) {
            signOut();
            return;
          }
          const terminal = terminalUndoFailure(error);
          reconcileStaleState(error, mapId);
          if (terminal) invalidateMap(mapId);
          say(
            `That could not be undone. ${messageFor(error)}`,
            terminal ? undefined : () => run(mapId, undoId),
          );
        }
      })();
    },
    [dismiss, invalidateMap, reconcileStaleState, rememberView, say, signOut, withBusyMutation],
  );

  const undoTidy = useCallback(() => {
    const current = openNow.current;
    if (current === null || current.arrangementUndoId === null) return;
    undoArrangement(current.map.id, current.arrangementUndoId);
  }, [undoArrangement]);

  const startMap = useCallback(
    async (topic: string): Promise<boolean> => {
      const owner = accountId.current;
      if (owner === null) return false;
      const navigationAtStart = navigation.current;
      const key = topic;
      const pending = mapCreationAction.current;
      if (pending?.outcomeUnknown === true && pending.key !== key) {
        say('Try starting the previous map again before starting another one.');
        return false;
      }
      const actionId =
        pending?.key === key && (pending.outcomeUnknown || pending.navigation === navigationAtStart)
          ? pending.id
          : crypto.randomUUID();
      if (
        !rememberMapCreationAction({
          key,
          id: actionId,
          navigation: navigationAtStart,
          outcomeUnknown: pending?.id === actionId && pending.outcomeUnknown,
        })
      ) {
        say('That map was not started. This browser could not save its retry information.');
        return false;
      }
      const made = await write(
        () => api.createMap(topic, actionId, owner),
        (why) => `That map was not started. ${why}`,
        undefined,
        (error) => {
          const current = mapCreationAction.current;
          if (current?.id !== actionId) return;
          if (responseIsUnknown(error)) {
            rememberMapCreationAction({ ...current, outcomeUnknown: true });
            setMapCreationPending(topic);
          } else {
            rememberMapCreationAction(null);
            setMapCreationPending(null);
            hydrateStoredCreationActions();
          }
        },
      );
      if (made === null) return false;
      if (mapCreationAction.current?.id === actionId) rememberMapCreationAction(null);
      setMapCreationPending(null);
      hydrateStoredCreationActions();
      updateStarterDraft((held) => (held === topic ? '' : held));
      moveMap(made);
      const stillOnList =
        !made.deleted &&
        activeNow.current &&
        navigation.current === navigationAtStart &&
        openIdNow.current === null;
      if (stillOnList) {
        openIdNow.current = made.id;
        setOpenId(made.id);
        setOpen({
          map: made,
          hasManualPositions: false,
          renderScale: null,
          renderOriginX: '0',
          renderOriginY: '0',
          renderSpan: null,
          arrangementUndoId: null,
          arrangementAction: null,
          nodes: [],
          deletedBranches: [],
        });
        setMapProblem(null);
        selectedNow.current = CENTRE;
        setSelected(CENTRE);
        setMapRevision((n) => n + 1);
      }
      setListRevision((n) => n + 1);
      return true;
    },
    [hydrateStoredCreationActions, moveMap, rememberMapCreationAction, say, write],
  );

  const removeMap = useCallback(
    async (map: MindMap): Promise<boolean> => {
      if (pendingMapDeletions.current.has(map.id)) return false;
      let requestWriteVersion: string | undefined;
      return runRecoverableTransition(
        { mapId: map.id },
        () => {
          requestWriteVersion ??= mapWriteVersions.current.get(map.id) ?? map.writeVersion;
          return api.deleteMap(map.id, map.version, requestWriteVersion);
        },
        (gone) => {
          closeMapIfOpen(gone.id);
          moveMap(gone);
          setListRevision((n) => n + 1);
          say(
            `"${map.heading}" deleted.`,
            undoWith(async () => {
              const restored = await api.updateMap(map.id, {
                deleted: false,
                version: gone.version,
                writeVersion: gone.writeVersion,
                inverseOf: gone.version,
              });
              moveMap(restored);
            }, map.id),
          );
        },
        (why) => `"${map.heading}" was not deleted. ${why}`,
        map.id,
      );
    },
    [closeMapIfOpen, moveMap, runRecoverableTransition, say, undoWith],
  );

  const restoreMap = useCallback(
    async (map: MindMap) => {
      const back = await writeRetryably(
        () =>
          api.updateMap(map.id, {
            deleted: false,
            version: map.version,
            writeVersion: map.writeVersion,
          }),
        (why) => `"${map.heading}" was not put back. ${why}`,
        (why) => `"${map.heading}" may already be back. ${why}`,
        map.id,
        'resource',
      );
      if (back === null) return;
      moveMap(back);
      if (openIdNow.current === map.id) {
        setOpen(null);
        setMapProblem(null);
        setMapRevision((n) => n + 1);
      }
      setListRevision((n) => n + 1);
      say(
        `"${map.heading}" is back.`,
        undoWith(
          async () => {
            const deleted = await api.deleteMap(
              map.id,
              back.version,
              back.writeVersion,
              back.version,
            );
            closeMapIfOpen(deleted.id);
            moveMap(deleted);
          },
          map.id,
          { mapId: map.id },
        ),
      );
    },
    [closeMapIfOpen, moveMap, say, undoWith, writeRetryably],
  );

  const completeBranchCreation = useCallback(
    async (action: PendingBranchCreationAction): Promise<boolean> => {
      const resolvingUnknown = action.outcomeUnknown;
      const made = await write(
        () =>
          api.createBranch(action.mapId, {
            actionId: action.id,
            parentId: action.parentId,
            label: action.label,
          }),
        (why) => `"${action.label}" was not added. ${why}`,
        action.mapId,
        (error) => {
          const current = branchCreationAction.current;
          if (current?.id !== action.id) return;
          if (responseIsUnknown(error)) {
            rememberBranchCreationAction({ ...current, outcomeUnknown: true });
            setBranchCreationPending({
              mapId: action.mapId,
              targetId: action.parentId ?? CENTRE,
              label: action.label,
            });
          } else {
            rememberBranchCreationAction(null);
            setBranchCreationPending(null);
            hydrateStoredCreationActions();
          }
        },
      );
      if (made === null) return false;
      if (branchCreationAction.current?.id === action.id) rememberBranchCreationAction(null);
      setBranchCreationPending(null);
      hydrateStoredCreationActions();
      if (resolvingUnknown) {
        setRetryFocus({
          mapId: action.mapId,
          destination: made.state === 'map-deleted' ? 'resource' : 'composer',
        });
      }
      if (made.state === 'map-deleted') {
        acceptDeletedMap(action.mapId);
        return true;
      }
      rememberMapWriteVersion(action.mapId, made.mapVersion);
      if (made.state === 'branch-deleted') {
        if (openIdNow.current === action.mapId) {
          setOpen(null);
          setMapProblem(null);
        }
        invalidateMap(action.mapId);
        return true;
      }
      rememberNode(action.mapId, made.node, true);
      invalidateMap(action.mapId);
      // Deliberately SILENT on success, and it is the one write on this screen
      // that is. The branch appears on the map under the cursor's own eyes -
      // which is the test every other toast in this client exists to pass - and
      // brainstorming is a burst of them, so a toast per branch would be a
      // column of identical lines over the map they are about.
      return true;
    },
    [
      acceptDeletedMap,
      hydrateStoredCreationActions,
      invalidateMap,
      rememberBranchCreationAction,
      rememberMapWriteVersion,
      rememberNode,
      write,
    ],
  );

  const addBranch = useCallback(
    async (label: string): Promise<boolean> => {
      if (openId === null || branchCreationAction.current !== null) return false;
      const action: PendingBranchCreationAction = {
        key: JSON.stringify([openId, selectedHere === CENTRE ? null : selectedHere, label]),
        id: crypto.randomUUID(),
        navigation: navigation.current,
        outcomeUnknown: false,
        mapId: openId,
        parentId: selectedHere === CENTRE ? null : selectedHere,
        label,
      };
      if (!rememberBranchCreationAction(action)) {
        say(`"${label}" was not added. This browser could not save its retry information.`);
        return false;
      }
      return completeBranchCreation(action);
    },
    [completeBranchCreation, openId, rememberBranchCreationAction, say, selectedHere],
  );

  const retryBranchCreation = useCallback(async (): Promise<boolean> => {
    const action = branchCreationAction.current;
    if (action === null || !action.outcomeUnknown) return false;
    if (!persistBranchCreationAction()) {
      say(`"${action.label}" was not added. This browser could not save its retry information.`);
      return false;
    }
    return completeBranchCreation(action);
  }, [completeBranchCreation, persistBranchCreationAction, say]);

  const rename = useCallback(
    async (label: string, basis: RealizeRenameBasis): Promise<RealizeRenameBasis | null> => {
      const mapId = basis.mapId;
      const current = openNow.current;
      if (openIdNow.current !== mapId || current?.map.id !== mapId) {
        invalidateMap(mapId);
        say('That rename was not saved. Reload the map before saving this draft.');
        return null;
      }

      if (basis.targetId === CENTRE) {
        if (current.map.topic !== basis.value) {
          say(
            'That topic was not saved. It changed after this edit began. Review the latest words, then choose Rename again.',
          );
          return null;
        }
        const requestWriteVersion =
          current.map.writeVersion === basis.mapWriteVersion
            ? basis.mapWriteVersion
            : current.map.writeVersion;
        const saved = await writeRetryably(
          () => api.updateMap(mapId, { topic: label, writeVersion: requestWriteVersion }),
          (why) => `That topic was not saved. ${why}`,
          (why) => `That topic may already have been saved. ${why}`,
          mapId,
          'composer',
        );
        if (saved === null) return null;
        rememberMap(saved);
        setMapRevision((n) => n + 1);
        setListRevision((n) => n + 1);
        return {
          mapId,
          targetId: CENTRE,
          value: saved.topic,
          mapWriteVersion: saved.writeVersion,
        };
      }

      const branch = current.nodes.find((node) => node.id === basis.targetId);
      if (branch === undefined) {
        invalidateMap(mapId);
        say('That branch was not saved. It is no longer on this map.');
        return null;
      }
      if (branch.label !== basis.value) {
        say(
          'That branch was not saved. It changed after this edit began. Review the latest words, then choose Rename again.',
        );
        return null;
      }
      const requestMapVersion =
        current.map.writeVersion === basis.mapWriteVersion
          ? basis.mapWriteVersion
          : current.map.writeVersion;
      const saved = await writeRetryably(
        () => api.updateBranch(branch.id, { label, mapVersion: requestMapVersion }),
        (why) => `That branch was not saved. ${why}`,
        (why) => `That branch may already have been saved. ${why}`,
        mapId,
        'composer',
      );
      if (saved === null) return null;
      rememberNode(mapId, saved.node, false);
      rememberMapWriteVersion(mapId, saved.mapVersion);
      invalidateMap(mapId);
      return {
        mapId,
        targetId: saved.node.id,
        value: saved.node.label,
        mapWriteVersion: saved.mapVersion,
      };
    },
    [invalidateMap, rememberMap, rememberMapWriteVersion, rememberNode, say, writeRetryably],
  );

  const removeBranch = useCallback(async (): Promise<string | null> => {
    if (openId === null || selectedHere === CENTRE) return null;
    const mapId = openId;
    const target = selectedHere;
    const branch = open?.nodes.find((node: MindNode) => node.id === target) ?? null;
    const mapWriteVersion = open?.map.writeVersion;
    const named =
      branch === null || branch.label.trim() === '' ? 'That branch' : `"${branch.label}"`;
    if (branch === null || mapWriteVersion === undefined) {
      invalidateMap(mapId);
      return null;
    }
    if (pendingBranchDeletions.current.has(target)) return null;

    let requestMapVersion: string | undefined;
    let landedSelection: string | null = null;
    const completed = await runRecoverableTransition(
      { branchId: target },
      () => {
        requestMapVersion ??= mapWriteVersions.current.get(mapId) ?? mapWriteVersion;
        return api.deleteBranch(target, branch.version, requestMapVersion);
      },
      (gone) => {
        const latestSelection = selectedNow.current;
        const selectionWasRemoved =
          latestSelection !== CENTRE &&
          !gone.view.nodes.some((node) => node.id === latestSelection);
        const parent = branch.parentId ?? CENTRE;
        const landed =
          parent === CENTRE || gone.view.nodes.some((node) => node.id === parent) ? parent : CENTRE;
        landedSelection = selectionWasRemoved ? landed : null;
        rememberView(gone.view, landed);
        invalidateMap(mapId);

        if (gone.removed === 0) {
          say(`${named} was already deleted.`);
          return;
        }

        // The SERVER'S count, not the tree this client is holding: it is the number
        // that actually went, and it is what the Undo puts back.
        const under = gone.removed - 1;
        say(
          under > 0 ? `${named} deleted, and the ${under} under it.` : `${named} deleted.`,
          undoWith(async () => {
            const restored = await api.restoreBranch(
              target,
              gone.node.version,
              gone.view.map.writeVersion,
              gone.node.version,
            );
            rememberView(restored.view);
          }, mapId),
        );
      },
      (why) => `${named} was not deleted. ${why}`,
      mapId,
    );
    return completed ? landedSelection : null;
  }, [
    invalidateMap,
    open,
    openId,
    rememberView,
    runRecoverableTransition,
    say,
    selectedHere,
    undoWith,
  ]);

  const restoreBranch = useCallback(
    async (branch: DeletedBranch) => {
      if (openId === null || open === null) return;
      const mapId = openId;
      const back = await writeRetryably(
        () =>
          api.restoreBranch(
            branch.id,
            branch.version,
            mapWriteVersions.current.get(mapId) ?? open.map.writeVersion,
          ),
        (why) => `"${branch.heading}" was not put back. ${why}`,
        (why) => `"${branch.heading}" may already be back. ${why}`,
        mapId,
        'resource',
      );
      if (back === null) return;
      rememberView(back.view);
      invalidateMap(mapId);
      say(
        `"${branch.heading}" is back.`,
        undoWith(
          async () => {
            const deleted = await api.deleteBranch(
              branch.id,
              back.node.version,
              back.view.map.writeVersion,
              back.node.version,
            );
            rememberView(deleted.view);
          },
          mapId,
          { branchId: branch.id },
        ),
      );
    },
    [invalidateMap, open, openId, rememberView, say, undoWith, writeRetryably],
  );

  const moveBranch = useCallback(
    async (id: string, at: PositionWrite) => {
      if (openId === null) return;
      const mapId = openId;
      const actionId = crypto.randomUUID();
      tidyActions.current.delete(mapId);
      // OPTIMISTIC, held in the payload this screen is already drawing from, so
      // the box stays where it was put while the write is in the air. Without
      // it, the map re-reads and the branch jumps back to its computed place for
      // as long as the round trip takes - which reads as the drag not working.
      const mine: PlacedByHand = {
        ...at,
        acknowledged: false,
        sequence: ++placementSequence.current,
      };
      const currentView = openNow.current?.map.id === mapId ? openNow.current : null;
      const priorRenderScale = currentView?.renderScale ?? null;
      const priorRenderOriginX = currentView?.renderOriginX ?? '0';
      const priorRenderOriginY = currentView?.renderOriginY ?? '0';
      const priorRenderSpan = currentView?.renderSpan ?? null;
      let confirmed = confirmedPositions.current.get(mapId);
      if (confirmed === undefined) {
        confirmed = new Map();
        confirmedPositions.current.set(mapId, confirmed);
      }
      if (!confirmed.has(id)) {
        const node = currentView?.nodes.find((candidate) => candidate.id === id);
        if (node !== undefined) {
          confirmed.set(id, {
            x: node.x,
            y: node.y,
            positionSpace: node.positionSpace,
            anchorX: node.anchorX,
            anchorY: node.anchorY,
            renderDeltaX: node.renderDeltaX,
            renderDeltaY: node.renderDeltaY,
            renderOriginX: node.renderOriginX,
            renderOriginY: node.renderOriginY,
          });
        }
      }
      if (!confirmedManualPositions.current.has(mapId) && currentView !== null) {
        confirmedManualPositions.current.set(mapId, currentView.hasManualPositions);
      }
      let mapPlacements = placed.current.get(mapId);
      if (mapPlacements === undefined) {
        mapPlacements = new Map();
        placed.current.set(mapId, mapPlacements);
      }
      mapPlacements.set(id, mine);
      setOpen((held) =>
        held === null || held.map.id !== mapId
          ? held
          : {
              ...held,
              hasManualPositions: true,
              renderScale: at.renderScale,
              renderOriginX: at.mapRenderOriginX,
              renderOriginY: at.mapRenderOriginY,
              renderSpan: at.mapRenderSpan,
              nodes: held.nodes.map((node: MindNode) =>
                node.id === id
                  ? {
                      ...node,
                      x: at.x,
                      y: at.y,
                      positionSpace: at.positionSpace,
                      anchorX: at.anchorX,
                      anchorY: at.anchorY,
                      renderDeltaX: at.renderDeltaX,
                      renderDeltaY: at.renderDeltaY,
                      renderOriginX: at.renderOriginX,
                      renderOriginY: at.renderOriginY,
                    }
                  : node,
              ),
            },
      );

      const fallbackMapVersion = currentView?.map.writeVersion;
      let requestMapVersion: string | undefined;
      const acceptMove = (saved: {
        node: MindNode;
        mapVersion: string;
        renderScale: number | null;
        renderOriginX: MindNode['renderOriginX'];
        renderOriginY: MindNode['renderOriginY'];
        renderSpan: MapView['renderSpan'];
        hasManualPositions: boolean;
        arrangementUndoId: string | null;
        arrangementAction: MapView['arrangementAction'];
        view: MapView | null;
      }): void => {
        let known = confirmedPositions.current.get(mapId);
        if (known === undefined) {
          known = new Map();
          confirmedPositions.current.set(mapId, known);
        }
        known.set(id, {
          x: saved.node.x,
          y: saved.node.y,
          positionSpace: saved.node.positionSpace,
          anchorX: saved.node.anchorX,
          anchorY: saved.node.anchorY,
          renderDeltaX: saved.node.renderDeltaX,
          renderDeltaY: saved.node.renderDeltaY,
          renderOriginX: saved.node.renderOriginX,
          renderOriginY: saved.node.renderOriginY,
        });
        if (saved.node.x !== null && saved.node.y !== null) {
          mine.x = saved.node.x;
          mine.y = saved.node.y;
          mine.anchorX = saved.node.anchorX;
          mine.anchorY = saved.node.anchorY;
          mine.renderDeltaX = saved.node.renderDeltaX;
          mine.renderDeltaY = saved.node.renderDeltaY;
          mine.renderOriginX = saved.node.renderOriginX;
          mine.renderOriginY = saved.node.renderOriginY;
        }
        if (saved.view === null) {
          mine.acknowledged = true;
          rememberNode(mapId, saved.node, false);
        } else {
          const current = placed.current.get(mapId);
          if (current !== undefined) {
            for (const [nodeId, placement] of current) {
              if (placement === mine || placement.acknowledged) current.delete(nodeId);
            }
            if (current.size === 0) placed.current.delete(mapId);
          }
          rememberView(saved.view, CENTRE, true);
        }
        if (saved.view === null) {
          setOpen((held) =>
            held === null || held.map.id !== mapId
              ? held
              : {
                  ...held,
                  renderScale: saved.renderScale,
                  renderOriginX: saved.renderOriginX,
                  renderOriginY: saved.renderOriginY,
                  renderSpan: saved.renderSpan,
                  hasManualPositions: saved.hasManualPositions,
                  arrangementUndoId: saved.arrangementUndoId,
                  arrangementAction: saved.arrangementAction,
                },
          );
        }
        rememberMapWriteVersion(mapId, saved.mapVersion);
        confirmedManualPositions.current.set(mapId, true);
        invalidateMap(mapId);
        releaseClosedMapState(mapId);
      };
      const saved = await writeRetryably(
        () => {
          requestMapVersion ??= mapWriteVersions.current.get(mapId) ?? fallbackMapVersion;
          if (requestMapVersion === undefined) throw new Error('open map has no write version');
          return api.updateBranch(id, {
            actionId,
            x: at.x,
            y: at.y,
            positionSpace: at.positionSpace,
            anchorX: at.anchorX,
            anchorY: at.anchorY,
            renderDeltaX: at.renderDeltaX,
            renderDeltaY: at.renderDeltaY,
            renderOriginX: at.renderOriginX,
            renderOriginY: at.renderOriginY,
            renderScale: at.renderScale,
            mapRenderOriginX: at.mapRenderOriginX,
            mapRenderOriginY: at.mapRenderOriginY,
            mapRenderSpan: at.mapRenderSpan,
            mapVersion: requestMapVersion,
          });
        },
        (why) => `That branch was not moved. ${why}`,
        (why) => `That branch may already have been moved. ${why}`,
        mapId,
        'resource',
        false,
        acceptMove,
      );
      if (saved !== null) return;

      const current = placed.current.get(mapId);
      const shouldRollBack = current?.get(id) === mine;
      if (shouldRollBack) current.delete(id);
      if (current?.size === 0) placed.current.delete(mapId);
      if (shouldRollBack) {
        const position = confirmedPositions.current.get(mapId)?.get(id);
        if (position !== undefined) {
          setOpen((held) =>
            held === null || held.map.id !== mapId
              ? held
              : {
                  ...held,
                  hasManualPositions:
                    (placed.current.get(mapId)?.size ?? 0) > 0 ||
                    (confirmedManualPositions.current.get(mapId) ?? false),
                  renderScale: priorRenderScale,
                  renderOriginX: priorRenderOriginX,
                  renderOriginY: priorRenderOriginY,
                  renderSpan: priorRenderSpan,
                  nodes: held.nodes.map((node) =>
                    node.id === id
                      ? {
                          ...node,
                          x: position.x,
                          y: position.y,
                          positionSpace: position.positionSpace,
                          anchorX: position.anchorX,
                          anchorY: position.anchorY,
                          renderDeltaX: position.renderDeltaX,
                          renderDeltaY: position.renderDeltaY,
                          renderOriginX: position.renderOriginX,
                          renderOriginY: position.renderOriginY,
                        }
                      : node,
                  ),
                },
          );
        }
      }
      releaseClosedMapState(mapId);
      invalidateMap(mapId);
    },
    [
      invalidateMap,
      openId,
      releaseClosedMapState,
      rememberMapWriteVersion,
      rememberNode,
      rememberView,
      writeRetryably,
    ],
  );

  const tidyUp = useCallback(
    async (was: readonly NodePosition[]) => {
      if (openId === null) return;

      const mapId = openId;
      const fallbackMapVersion =
        mapWriteVersions.current.get(mapId) ??
        (openNow.current?.map.id === mapId ? openNow.current.map.writeVersion : undefined);
      if (fallbackMapVersion === undefined) return;
      const pendingAction = tidyActions.current.get(mapId);
      const beginAction = (priorActionId: string | null): PendingTidyAction => {
        const action = { id: crypto.randomUUID(), priorActionId };
        tidyActions.current.set(mapId, action);
        return action;
      };
      const action =
        pendingAction ??
        beginAction(
          openNow.current?.map.id === mapId
            ? (openNow.current.arrangementAction?.actionId ?? null)
            : null,
        );
      const cleared = new Set(placed.current.get(mapId)?.values() ?? []);
      const attempt = await write(
        async () => {
          try {
            const result = await api.tidyMap(
              mapId,
              action.id,
              mapWriteVersions.current.get(mapId) ?? fallbackMapVersion,
            );
            return { kind: 'tidy' as const, result };
          } catch (error) {
            if (pendingAction === undefined || refusalRule(error) !== 'realize-action-superseded') {
              throw error;
            }
            return { kind: 'resolved' as const, view: await api.map(mapId) };
          }
        },
        (why) => `That map was not tidied up. ${why}`,
        mapId,
        (error) => {
          if (
            refusalRule(error) === 'realize-action-superseded' ||
            (error instanceof ApiClientError && error.status === 404)
          ) {
            tidyActions.current.delete(mapId);
          }
        },
      );
      if (attempt === null) return;
      tidyActions.current.delete(mapId);

      if (attempt.kind === 'resolved') {
        placed.current.delete(mapId);
        rememberView(attempt.view, CENTRE, true);
        invalidateMap(mapId);
        say('The map changed since that tidy-up. Review it, then choose Tidy up again.');
        return;
      }

      const { result } = attempt;
      const current = placed.current.get(mapId);
      if (current !== undefined) {
        for (const [id, placement] of current) {
          if (cleared.has(placement)) current.delete(id);
        }
        if (current.size === 0) placed.current.delete(mapId);
      }
      const stillPositioned = new Set(
        result.view.nodes
          .filter((node) => node.x !== null && node.y !== null)
          .map((node) => node.id),
      );
      const clearedVisible = was.filter((node) => !stillPositioned.has(node.id)).length;
      rememberView(result.view, CENTRE, true);
      invalidateMap(mapId);

      const undoId = result.undoId;
      if (result.undoState === 'restored') {
        say('The earlier arrangement was already put back.');
        return;
      }
      if (result.undoState === 'none' || undoId === null) {
        say('Those branches were already back where the map arranges them.');
        return;
      }
      say(
        pendingAction !== undefined && was.length === 0
          ? 'The earlier arrangement can be put back with Undo.'
          : was.length === 0
            ? "Deleted branches will use the map's arrangement when restored."
            : clearedVisible === 0
              ? 'The earlier arrangement can be put back with Undo.'
              : clearedVisible === 1
                ? 'One branch put back where the map arranges it.'
                : `${clearedVisible} branches put back where the map arranges them.`,
        () => undoArrangement(mapId, undoId),
      );
    },
    [invalidateMap, openId, rememberView, say, undoArrangement, write],
  );

  return {
    today,
    maps,
    deletedMaps,
    problem,
    open,
    openId,
    mapProblem,
    selected: selectedHere,
    select,
    composer,
    updateComposer,
    busy,
    starterDraft,
    updateStarterDraft,
    mapCreationPending,
    branchCreationPending,
    retryBranchCreation,
    retryProblem,
    retryPending,
    retryFocus,
    consumeRetryFocus,
    deletingMapIds,
    deletingBranchIds,
    openMap,
    closeMap,
    refresh,
    startMap,
    removeMap,
    restoreMap,
    addBranch,
    rename,
    removeBranch,
    restoreBranch,
    moveBranch,
    tidyUp,
    undoTidy,
  };
}
